import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/lib/CartContext";
import { formatApiErrorDetail } from "@/lib/AdminAuthContext";
import { ShieldCheck, Truck, MapPin, Lock, ArrowLeft } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmt = (n) => `$${Number(n).toFixed(2)}`;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC","PR",
];

export default function Checkout() {
  const navigate = useNavigate();
  const { detailedItems, subtotal, items, loading: cartLoading } = useCart();
  const [form, setForm] = useState({
    email: "",
    name: "",
    phone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    postal_code: "",
    country: "US",
    marketing_opt_in: true,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Redirect to /cart if empty
  useEffect(() => {
    if (!cartLoading && items.length === 0) {
      const t = setTimeout(() => navigate("/cart"), 500);
      return () => clearTimeout(t);
    }
  }, [items.length, cartLoading, navigate]);

  // Show toast on cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled") === "true") {
      toast("Payment canceled. Review and try again.");
    }
  }, []);

  const total = subtotal;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
    if (!form.name.trim()) e.name = "Full name required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid phone required";
    if (!form.line1.trim()) e.line1 = "Street address required";
    if (!form.city.trim()) e.city = "City required";
    if (!form.state.trim()) e.state = "State required";
    if (!form.postal_code.trim()) e.postal_code = "ZIP required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        customer: {
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          phone: form.phone.trim(),
          shipping: {
            line1: form.line1.trim(),
            line2: form.line2.trim(),
            city: form.city.trim(),
            state: form.state.trim().toUpperCase(),
            postal_code: form.postal_code.trim(),
            country: form.country,
          },
          marketing_opt_in: form.marketing_opt_in,
        },
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/orders`, payload);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start payment. Try again.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080412] text-white font-sans" data-testid="checkout-page">
      <header className="border-b border-white/8 bg-[#080412]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="checkout-brand-logo">
            <img src="/assets/hero-logo.png" alt="House Rules" className="h-12 md:h-14 w-auto rounded-lg drop-shadow-[0_0_20px_rgba(167,66,255,0.5)]" />
          </Link>
          <Link to="/cart" className="font-sora text-[10.5px] font-semibold uppercase tracking-[0.24em] flex items-center gap-2 text-white/80 hover:text-brandpink transition-colors" data-testid="checkout-back-to-cart">
            <ArrowLeft size={14} weight="bold" /> Back to Cart
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 md:py-14">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-brandpink mb-2">Checkout · Step 2 of 2</div>
        <h1 className="font-display uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-[-0.01em] mb-10" data-testid="checkout-headline">
          Where do we <span className="italic font-editorial text-brandpink hr-text-glow-pink">ship it?</span>
        </h1>

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Section title="Contact" icon={<ShieldCheck size={18} weight="duotone" className="text-brandpink" />}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" testid="field-email" error={errors.email}>
                  <input type="email" autoComplete="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls(errors.email)} data-testid="input-email" />
                </Field>
                <Field label="Full name" testid="field-name" error={errors.name}>
                  <input autoComplete="name" value={form.name} onChange={(e) => setField("name", e.target.value)} className={inputCls(errors.name)} data-testid="input-name" />
                </Field>
                <Field label="Phone (for shipping updates)" testid="field-phone" error={errors.phone} full>
                  <input type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls(errors.phone)} data-testid="input-phone" />
                </Field>
              </div>
            </Section>

            <Section title="Shipping address" icon={<MapPin size={18} weight="duotone" className="text-brandpink" />}>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Street address" testid="field-line1" error={errors.line1} full>
                  <input autoComplete="address-line1" value={form.line1} onChange={(e) => setField("line1", e.target.value)} className={inputCls(errors.line1)} data-testid="input-line1" />
                </Field>
                <Field label="Apt, suite, etc. (optional)" testid="field-line2" full>
                  <input autoComplete="address-line2" value={form.line2} onChange={(e) => setField("line2", e.target.value)} className={inputCls(false)} data-testid="input-line2" />
                </Field>
                <Field label="City" testid="field-city" error={errors.city}>
                  <input autoComplete="address-level2" value={form.city} onChange={(e) => setField("city", e.target.value)} className={inputCls(errors.city)} data-testid="input-city" />
                </Field>
                <Field label="State" testid="field-state" error={errors.state}>
                  <select autoComplete="address-level1" value={form.state} onChange={(e) => setField("state", e.target.value)} className={inputCls(errors.state)} data-testid="input-state">
                    <option value="">Choose state</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="ZIP code" testid="field-zip" error={errors.postal_code}>
                  <input autoComplete="postal-code" value={form.postal_code} onChange={(e) => setField("postal_code", e.target.value)} className={inputCls(errors.postal_code)} data-testid="input-postal_code" />
                </Field>
                <Field label="Country" testid="field-country">
                  <select value={form.country} onChange={(e) => setField("country", e.target.value)} className={inputCls(false)} data-testid="input-country">
                    <option value="US">United States</option>
                  </select>
                </Field>
              </div>
              <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="marketing-optin-label">
                <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setField("marketing_opt_in", e.target.checked)} className="mt-1 w-4 h-4 rounded border-white/25 bg-white/5 accent-[#FF2C8C]" data-testid="input-marketing" />
                <span className="font-sans text-sm text-white/70">Send me game-night ideas, new deck releases and exclusive offers. Unsubscribe anytime.</span>
              </label>
            </Section>

            <div className="text-sm font-sans text-white/60 flex items-center gap-2 pt-2">
              <Lock size={14} weight="duotone" className="text-brandpink" /> Payment is handled securely by Stripe on the next screen. We never see your card details.
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-2xl hr-glass hr-neon-pink p-6 sticky top-24" data-testid="checkout-summary">
              <div className="font-display uppercase text-2xl mb-4 text-white">Order summary</div>
              <div className="space-y-3 mb-5">
                {detailedItems.map((it) => (
                  <div key={it.product_id} className="flex gap-3 items-center" data-testid="summary-item">
                    <img src={it.product.image} alt="" className="w-14 h-14 rounded-lg border border-white/12 object-cover" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display uppercase text-sm truncate text-white">{it.product.name}</div>
                      <div className="font-sans text-xs text-white/50">Qty {it.quantity}</div>
                    </div>
                    <div className="font-anton text-base text-white">{fmt(it.lineTotal)}</div>
                  </div>
                ))}
              </div>
              <SummaryRow label="Subtotal" value={fmt(subtotal)} testid="summary-subtotal" />
              <SummaryRow label="Shipping" value="FREE" />
              <div className="border-t border-white/12 my-3" />
              <SummaryRow label="Total" value={fmt(total)} bold testid="summary-total" />

              <button type="submit" disabled={submitting || detailedItems.length === 0}
                className="hr-cta w-full mt-5 h-12 py-3 rounded-full text-white font-sora font-semibold text-sm uppercase tracking-[0.22em] flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="checkout-pay-button">
                <Lock size={14} weight="duotone" /> {submitting ? "Preparing…" : "Pay Securely"}
              </button>

              <div className="mt-3 flex items-center gap-2 text-xs text-white/50 font-sans">
                <Truck size={14} weight="duotone" /> Free US shipping · ships in 3–5 days
              </div>
            </div>
          </aside>
        </form>
      </main>
    </div>
  );
}

const Section = ({ title, icon, children }) => (
  <div className="rounded-2xl hr-glass p-6 sm:p-7">
    <div className="flex items-center gap-2 mb-5">
      {icon}
      <div className="font-display uppercase text-2xl text-white">{title}</div>
    </div>
    {children}
  </div>
);

const Field = ({ label, error, children, full, testid }) => (
  <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
    <label className="font-sora text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60 mb-1.5 block">{label}</label>
    {children}
    {error && <div className="mt-1 text-xs text-brandpink font-sans">{error}</div>}
  </div>
);

const inputCls = (hasError) =>
  `w-full px-4 py-2.5 font-sans text-base bg-white/[0.04] border rounded-lg focus:outline-none transition text-white placeholder-white/40 ${
    hasError ? "border-brandpink focus:border-brandpink" : "border-white/15 focus:border-brandpink"
  }`;

const SummaryRow = ({ label, value, bold, testid }) => (
  <div className="flex items-center justify-between py-1">
    <span className="font-sans text-sm text-white/70">{label}</span>
    <span className={bold ? "font-anton text-2xl text-white" : "font-sora font-semibold text-white"} data-testid={testid}>
      {value}
    </span>
  </div>
);
